import React, { FC, ReactNode, useMemo, useState, isValidElement } from 'react';
import { Helmet } from 'react-helmet-async';
import { AuxiliaryPanelContext } from './auxiliary-panel-utils';
import {
  StyledPage,
  Content,
  ContentWrapper,
  Layout,
  Progress,
  Sider,
  PageWrapper,
  AuxiliaryPanel,
} from './components';
import { FilterBar } from '../../molecules/filter-bar';
import { Page } from '../../molecules/page';

export interface ArgonProps {
  documentTitle?: string;
  title: ReactNode;
  peel?: ReactNode;
  tabs?: ReactNode;
  sider?: ReactNode;
  breadcrumbs?: ReactNode;
  progress?: number;
  content: ReactNode;
  extraContent?: ReactNode;
  actions: { primary?: ReactNode; secondary?: ReactNode; central?: ReactNode };
  className?: string;
  whiteHeader?: boolean;
  description?: ReactNode;
  showHeaderBorder?: boolean;
  pageActions?: ReactNode;
  auxiliaryPanel?: ReactNode;
  gray?: boolean;
}
const outlineWidth = 0;
const innerOutlineWidth = 2;

const isNodeEmpty = (node: ReactNode): boolean => {
  if (node == null || node === false || node === '') {
    return true;
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return false;
  }

  if (Array.isArray(node)) {
    return node.length === 0 || node.every(isNodeEmpty);
  }

  if (isValidElement(node)) {
    const props = node.props as any;
    const children = props?.children;

    // Check if it's a React fragment
    if (node.type === React.Fragment) {
      // For fragments, check if children are empty
      if (children == null) {
        return true;
      }

      return isNodeEmpty(children);
    }

    // For other React elements, if they have children, check the children
    if (children != null) {
      return isNodeEmpty(children);
    }

    // If no children but element exists, check if it has any meaningful props
    // Elements with props (like icon, title, etc.) are considered non-empty
    const propKeys = Object.keys(props || {});

    // If there are props, the element is meaningful (not empty)
    return propKeys.length === 0;
  }

  return false;
};

export const Argon: FC<React.PropsWithChildren<ArgonProps>> = ({
  documentTitle,
  title,
  peel,
  tabs,
  sider,
  breadcrumbs,
  progress,
  content,
  extraContent,
  actions: { primary, secondary, central },
  className,
  whiteHeader,
  description,
  showHeaderBorder = true,
  pageActions,
  auxiliaryPanel,
  gray = false,
}) => {
  const [auxiliaryContent, setAuxiliaryContent] = useState<ReactNode | null>(
    auxiliaryPanel,
  );

  const contextValue = useMemo(
    () => ({
      setContent: (content: ReactNode, force?: boolean) => {
        setAuxiliaryContent(force ? content : (auxiliaryPanel ?? content));
      },
      clearContent: (force?: boolean) =>
        setAuxiliaryContent(force ? null : (auxiliaryPanel ?? null)),
    }),
    [auxiliaryPanel],
  );

  return (
    <AuxiliaryPanelContext.Provider value={contextValue}>
      <PageWrapper
        $hasAuxiliaryPanel={auxiliaryContent != null}
        $outlineWidth={outlineWidth}
        className={className}
      >
        <StyledPage
          $showHeaderBorder={showHeaderBorder}
          outlineWidth={innerOutlineWidth}
        >
          {peel != null ? <Page.Peel>{peel}</Page.Peel> : null}
          {!(
            isNodeEmpty(title) &&
            isNodeEmpty(description) &&
            isNodeEmpty(breadcrumbs) &&
            isNodeEmpty(tabs) &&
            isNodeEmpty(pageActions)
          ) ? (
            <Page.Header
              $white={whiteHeader}
              title={title}
              subText={description}
              breadcrumbs={breadcrumbs}
              tabs={tabs}
              actions={pageActions}
            />
          ) : null}
          <Helmet>
            <title>{documentTitle}</title>
          </Helmet>
          <Page.Content>
            <Layout>
              {sider != null ? <Sider width={256}>{sider}</Sider> : null}
              <Content>
                {(primary ?? secondary ?? central != null) ? (
                  <FilterBar>
                    {primary != null ? (
                      <FilterBar.Primary>{primary}</FilterBar.Primary>
                    ) : null}
                    {central != null ? (
                      <FilterBar.Central>{central}</FilterBar.Central>
                    ) : null}
                    {secondary != null ? (
                      <FilterBar.Secondary>{secondary}</FilterBar.Secondary>
                    ) : null}
                  </FilterBar>
                ) : null}
                <ContentWrapper
                  $full={(primary ?? secondary ?? central) == null}
                  $gray={gray}
                >
                  <Progress progress={progress} />
                  {content}
                </ContentWrapper>
                {extraContent}
              </Content>
            </Layout>
          </Page.Content>
        </StyledPage>
        {auxiliaryContent != null ? (
          <AuxiliaryPanel $outlineWidth={innerOutlineWidth}>
            {auxiliaryContent}
          </AuxiliaryPanel>
        ) : null}
      </PageWrapper>
    </AuxiliaryPanelContext.Provider>
  );
};
